import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import ComboSelect from '@atom/components/common/comboSelect/ComboSelect';
import { GET_BUDGET_ITEM_TEMPLATE_FILTERS } from '@atom/graph/budget';
import { BudgetItemTemplate } from '@atom/types/budget';
import { objectIdsMatch } from '@atom/utilities/objectCompareUtilities';

import BudgetDetailContext from '../BudgetDetailContext';
import { DEBOUNCE_TIME } from '../budgetDetailUtils';
import { FILTER_KEYS, useBudgetFilterStorage } from '../useBudgetFilterStorage';

const LIMIT = 25;
const MIN_SEARCH_CHARS = 2;

const BudgetDetailItemFilter = () => {
  const {
    budgetItemTemplateFilters,
    setBudgetItemTemplateFilters,
    budget,
    parentBudgetUnit,
    loadingParentUnit,
    fetchParentUnit,
  } = useContext(BudgetDetailContext);

  const { existingBudgetItemFilters, saveFilterState } = useBudgetFilterStorage(
    budget.id,
  );

  const [itemFiltersCart, setItemFiltersCart] = useState<BudgetItemTemplate[]>(
    budgetItemTemplateFilters,
  );
  const [itemFiltersSnapshot, setItemFiltersSnapshot] = useState<
    BudgetItemTemplate[]
  >(budgetItemTemplateFilters);

  const [searchOptions, setSearchOptions] = useState<BudgetItemTemplate[]>([]);
  const [searchPage, setSearchPage] = useState<number>(1);
  const [searchTotal, setSearchTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [open, setOpen] = useState<boolean>();
  const [options, setOptions] = useState<BudgetItemTemplate[]>(
    existingBudgetItemFilters,
  );
  const [query, setQuery] = useState<string>('');

  const searching = useMemo(() => query.length >= MIN_SEARCH_CHARS, [query]);

  const [fetchOptions, { loading: loadingOptions, data: itemOptionsData }] =
    useLazyQuery(GET_BUDGET_ITEM_TEMPLATE_FILTERS, {
      fetchPolicy: 'network-only',
    });

  useEffect(() => {
    if (!itemOptionsData) {
      return;
    }
    const nextOptions = R.pathOr(
      [],
      ['budgetItemTemplatesFilter', 'budgetItemTemplates'],
      itemOptionsData,
    );
    const newTotal =
      itemOptionsData?.budgetItemTemplatesFilter?.totalCount || 0;
    if (searching) {
      setSearchOptions(prev => [...prev, ...nextOptions]);
      setSearchTotal(newTotal);
    } else {
      setOptions(prev => R.uniqBy(R.prop('name'), [...prev, ...nextOptions]));
      setTotal(newTotal);
    }
  }, [itemOptionsData]);

  const handleSetBudgetItemCart = (newTemplates: BudgetItemTemplate[]) => {
    setItemFiltersCart(newTemplates);
  };

  const handleSelectOption = (option: BudgetItemTemplate) => {
    const selectedIds = itemFiltersCart.map(template => template.id);
    const newTemplates = selectedIds.includes(option.id)
      ? itemFiltersCart.filter(template => template.id !== option.id)
      : [...itemFiltersCart, option];
    handleSetBudgetItemCart(newTemplates);
  };

  const handleClearSelections = () => {
    setBudgetItemTemplateFilters([]);
    handleSetBudgetItemCart([]);
    saveFilterState(FILTER_KEYS.ITEMS, []);
    fetchParentUnit({
      budgetUnitId: parentBudgetUnit?.id,
      newBudgetItemTemplateNames: [],
    });
  };

  const fetchOptionsDebounced = useCallback(
    debounce((value: string) => {
      fetchOptions({
        variables: {
          input: {
            budgetId: budget.id,
            name: value,
            limit: LIMIT,
          },
        },
      });
    }, DEBOUNCE_TIME),
    [],
  );

  const handleQueryChange = (value: string = '') => {
    setQuery(value);
    setSearchPage(1);
    setSearchOptions([]);
    if (!value) {
      fetchOptionsDebounced.cancel();
    } else if (value.length >= MIN_SEARCH_CHARS) {
      fetchOptionsDebounced(value);
    } else {
      fetchOptionsDebounced.cancel();
    }
  };

  const handlePageScroll = (nextPage: number) => {
    if (searching) {
      setSearchPage(nextPage);
    } else {
      setPage(nextPage);
    }

    fetchOptions({
      variables: {
        input: {
          budgetId: budget.id,
          limit: LIMIT,
          page: nextPage,
          name: query,
        },
      },
    });
  };

  useEffect(() => {
    // Cache initial selected item snapshot when user opens menu
    if (open === true) {
      setItemFiltersSnapshot(itemFiltersCart);
    }
    if (
      open === false &&
      !objectIdsMatch(itemFiltersSnapshot, itemFiltersCart)
    ) {
      setBudgetItemTemplateFilters(itemFiltersCart);
      saveFilterState(FILTER_KEYS.ITEMS, itemFiltersCart);
      fetchParentUnit({
        budgetUnitId: parentBudgetUnit?.id,
        newBudgetItemTemplateNames: itemFiltersCart.map(item => item?.name),
      });
    }
  }, [open]);

  useEffect(() => {
    fetchOptions({
      variables: {
        input: {
          budgetId: budget.id,
          limit: LIMIT,
        },
      },
    });
  }, []);

  return (
    <ComboSelect
      open={open}
      setOpen={setOpen}
      itemName="Budget Item"
      loading={loadingOptions}
      options={searching ? searchOptions : options}
      total={searching ? searchTotal : total}
      page={searching ? searchPage : page}
      handlePageScroll={handlePageScroll}
      query={query}
      onQueryChange={handleQueryChange}
      selectedOptions={itemFiltersCart}
      clearSelections={() => handleClearSelections()}
      disableClear={loadingParentUnit}
      onSelectOption={handleSelectOption}
      dataCyLabel="BudgetItemFilter"
    />
  );
};

export default BudgetDetailItemFilter;
